import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBrandContext } from "@/contexts/brand-context";
import {
  useCreateBrandAsset,
  useDeleteBrandAsset,
} from "@/hooks/use-brand-assets";
import { useUpdateAssetTags, useBrandAssetTags } from "@/hooks/use-library-assets";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/api-client";
import { optimizeImageForUpload } from "@/lib/image-optimize";
import { captureVideoPoster } from "@/lib/video-poster";
import { LibraryBrowser } from "@/components/media/library-browser";
import { TagInput } from "@/components/media/tag-input";
import { GenerateVideoDialog, type GenerateVideoInitial } from "@/components/media/generate-video-dialog";
import { AiVideoJobs } from "@/components/media/ai-video-jobs";
import {
  formatUsd,
  isActiveJob,
  useAiVideoJobs,
  useAiVideoOptions,
  useUpscaleVideo,
} from "@/hooks/use-ai-video";
import type { BrandAsset } from "@/hooks/use-brand-assets";
import { toast } from "sonner";
import { RotateCcw, Sparkles, Trash2, UploadCloud, Wand2 } from "lucide-react";

function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ width: 0, height: 0 });
    };
    img.src = url;
  });
}

const ACCEPTED = ["image/", "video/", "application/pdf"];
function isAccepted(file: File): boolean {
  return ACCEPTED.some((p) => file.type.startsWith(p));
}

export function BrandLibraryPage() {
  const { t } = useTranslation("pieces");
  const { brand } = useBrandContext();
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const dragDepth = useRef(0);

  const createAsset = useCreateBrandAsset(brand.id);
  const deleteAsset = useDeleteBrandAsset(brand.id);
  const updateTags = useUpdateAssetTags(brand.id);
  const tagSuggestions = useBrandAssetTags(brand.id).data ?? [];

  // Library → Generate video
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoInitial, setVideoInitial] = useState<GenerateVideoInitial | null>(null);
  const aiVideoOptions = useAiVideoOptions(brand.id).data;
  const aiVideoJobs = useAiVideoJobs(brand.id).data ?? [];
  const upscale = useUpscaleVideo(brand.id);

  const openVideoDialog = (initial: GenerateVideoInitial | null = null) => {
    setVideoInitial(initial);
    setVideoDialogOpen(true);
  };

  const isFinishing = (assetId: string) =>
    aiVideoJobs.some((j) => j.type === "video_upscale" && j.sourceAssetId === assetId && isActiveJob(j));

  const finishIn1080 = (a: BrandAsset) =>
    upscale.mutate(
      { sourceAssetId: a.id },
      {
        onSuccess: () => toast.success(t("pieces:library.aiVideo.toast.finishing")),
        onError: (err) => toast.error(err instanceof Error ? err.message : t("pieces:library.aiVideo.toast.failed")),
      },
    );

  /** Presign + PUT a file to S3; returns its public URL. */
  const presignAndPut = async (file: File): Promise<string> => {
    const presigned = await apiFetch<{ uploadUrl: string; publicUrl: string }>(
      `/brands/${brand.id}/upload-url?filename=${encodeURIComponent(file.name)}&contentType=${encodeURIComponent(file.type)}`,
    );
    const putRes = await fetch(presigned.uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    });
    if (!putRes.ok) throw new Error(`S3 upload failed: ${putRes.status}`);
    return presigned.publicUrl;
  };

  /** Upload a single file (presign → S3 PUT → create asset row). For videos we
   *  also capture a poster frame client-side and store it as the thumbnail. */
  const uploadOne = async (file: File): Promise<boolean> => {
    try {
      let toUpload = file;
      let width: number | undefined;
      let height: number | undefined;
      let thumbnailUrl: string | undefined;
      let durationSec: number | undefined;

      if (file.type.startsWith("image/")) {
        const { file: optimized } = await optimizeImageForUpload(file);
        toUpload = optimized;
        const dims = await readImageDimensions(optimized);
        width = dims.width || undefined;
        height = dims.height || undefined;
      }

      let posterFile: File | undefined;
      if (file.type.startsWith("video/")) {
        // Capture a poster frame locally (no upload yet). Best-effort.
        const poster = await captureVideoPoster(file);
        if (poster) {
          width = poster.width || undefined;
          height = poster.height || undefined;
          durationSec = poster.durationSec ? Math.round(poster.durationSec) : undefined;
          posterFile = poster.file;
        }
      }

      // Upload the main file first, so a failed video upload can't orphan a
      // poster blob in S3. Then upload the poster (best-effort).
      const url = await presignAndPut(toUpload);
      if (posterFile) {
        try {
          thumbnailUrl = await presignAndPut(posterFile);
        } catch {
          /* poster upload failed — keep the video without a thumbnail */
        }
      }

      const assetType: "IMAGE" | "VIDEO" | "DOCUMENT" = file.type.startsWith("video/")
        ? "VIDEO"
        : file.type === "application/pdf"
          ? "DOCUMENT"
          : "IMAGE";

      await createAsset.mutateAsync({
        url,
        type: assetType,
        filename: file.name,
        sizeBytes: toUpload.size,
        mimeType: toUpload.type,
        width,
        height,
        durationSec,
        thumbnailUrl,
        projectIds: [],
      });
      return true;
    } catch (err: any) {
      toast.error(err?.message ?? t("pieces:library.toast.uploadFailed"));
      return false;
    }
  };

  /** Upload a batch (file input or drop), sequentially. */
  const runUpload = async (files: File[]) => {
    const accepted = files.filter(isAccepted);
    if (accepted.length === 0) return;
    setUploading(true);
    let ok = 0;
    for (const f of accepted) {
      if (await uploadOne(f)) ok += 1;
    }
    setUploading(false);
    if (ok > 0) toast.success(t("pieces:library.toast.uploaded"));
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.currentTarget.value = "";
    if (files.length) runUpload(files);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    dragDepth.current = 0;
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length) runUpload(files);
  };

  return (
    <div
      className="relative mx-auto w-full max-w-7xl space-y-6 px-6 py-8"
      onDragEnter={(e) => {
        e.preventDefault();
        dragDepth.current += 1;
        setDragOver(true);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={() => {
        dragDepth.current -= 1;
        if (dragDepth.current <= 0) setDragOver(false);
      }}
      onDrop={handleDrop}
    >
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">{t("pieces:library.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("pieces:library.subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => openVideoDialog()}>
            <Sparkles className="h-4 w-4" />
            {t("pieces:library.aiVideo.open")}
          </Button>
          <label className="inline-block">
            <input
              type="file"
              accept="image/*,video/*,application/pdf"
              multiple
              className="hidden"
              onChange={handleFile}
            />
            <Button asChild disabled={uploading}>
              <span>
                {uploading ? t("pieces:library.uploading") : t("pieces:library.uploadAsset")}
              </span>
            </Button>
          </label>
        </div>
      </header>

      <AiVideoJobs brandId={brand.id} onRetry={openVideoDialog} />

      <LibraryBrowser
        brandId={brand.id}
        renderAssetFooter={(a) => (
          <div className="space-y-1 px-0.5 pt-1.5 text-xs">
            <div className="flex items-start justify-between gap-1">
              <div className="truncate" title={a.filename}>
                {a.filename}
              </div>
              <button
                onClick={() => {
                  if (confirm(t("pieces:library.confirmDelete", { filename: a.filename })))
                    deleteAsset.mutate(a.id);
                }}
                aria-label={t("pieces:library.deleteAsset")}
                className="text-destructive opacity-0 transition-opacity group-hover:opacity-100"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            {a.generation && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                    a.generation.stage === "FINAL"
                      ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300"
                      : "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300"
                  }`}
                >
                  {a.generation.stage === "FINAL"
                    ? t("pieces:library.aiVideo.badge.final", { resolution: a.generation.resolution })
                    : t("pieces:library.aiVideo.badge.draft", { resolution: a.generation.resolution })}
                </span>
                {a.generation.stage === "DRAFT" && !a.generation.finalAssetId && (
                  <button
                    type="button"
                    onClick={() => finishIn1080(a)}
                    disabled={isFinishing(a.id) || aiVideoOptions?.configured.upscale === false}
                    title={
                      aiVideoOptions?.configured.upscale === false
                        ? t("pieces:library.aiVideo.upscaleNotConfigured")
                        : undefined
                    }
                    className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] hover:bg-muted disabled:opacity-50"
                  >
                    <Wand2 className="h-3 w-3" />
                    {isFinishing(a.id)
                      ? t("pieces:library.aiVideo.finishing")
                      : t("pieces:library.aiVideo.finish", {
                          price: formatUsd(
                            (aiVideoOptions?.upscale.usdPerSecond ?? 0) *
                              (a.durationSec ?? a.generation.durationSeconds ?? 0),
                          ),
                        })}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() =>
                    openVideoDialog({
                      prompt: a.generation!.prompt,
                      model: a.generation!.model,
                      aspectRatio: a.generation!.aspectRatio,
                      durationSeconds: a.generation!.durationSeconds,
                      referenceAssetIds: a.generation!.referenceAssetIds,
                    })
                  }
                  className="inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] hover:bg-muted"
                >
                  <RotateCcw className="h-3 w-3" />
                  {t("pieces:library.aiVideo.tryAgain")}
                </button>
              </div>
            )}
            <TagInput
              value={a.tags}
              suggestions={tagSuggestions}
              onChange={(tags) => updateTags.mutate({ assetId: a.id, tags })}
            />
          </div>
        )}
      />

      <GenerateVideoDialog
        brandId={brand.id}
        open={videoDialogOpen}
        onOpenChange={setVideoDialogOpen}
        initial={videoInitial}
      />

      {/* Drag-to-upload overlay */}
      {dragOver && (
        <div className="pointer-events-none absolute inset-0 z-20 m-2 flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary bg-primary/5 text-primary backdrop-blur-sm">
          <UploadCloud className="h-10 w-10" />
          <span className="text-sm font-medium">{t("pieces:library.dropToUpload")}</span>
        </div>
      )}
    </div>
  );
}
